/*
 * -----------------------------------------------------------------------
 *  <copyright file="menu-content/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { SettingsMenuTypes } from '../settings-menu.interface';
import { MenuContentProps } from './menu-content.interface';
import { useTranslation } from '@hooks/index';
import './styles.scss';

const MenuContent = ({ items, onSubmit, onReset, checkedField, oneVisibleColumn }: MenuContentProps) => {
    const { t } = useTranslation();
    const isShowMenu = checkedField === SettingsMenuTypes.show;

    const [columns, setColumns] = React.useState<IExpandableGridColumnV2[]>(items);
    const [changed, setChanged] = useState(false);

    useEffect(() => {
        setColumns(items);
    }, [items]);

    const lockColumns = (column: IExpandableGridColumnV2): IExpandableGridColumnV2[] =>
        column.children?.map((item: IExpandableGridColumnV2) => {
            if (item.children?.length) {
                item.children = lockColumns(item);
            }

            return { ...item, locked: !item.locked, width: item.minWidth || 150 };
        }) || [];

    const toggleColumn = (itemId: string, field: SettingsMenuTypes) =>
        columns.map((column) => {
            if (column.id === itemId) {
                if (field === SettingsMenuTypes.lock && column.children?.length) {
                    column.children = lockColumns(column);
                }
                const totalMinWidth = column.children?.reduce((sum, item) => sum + item?.minWidth, 0);

                return {
                    ...column,
                    [field]: !column[field],
                    ...(totalMinWidth && column.children?.length ? { minWidth: totalMinWidth } : null),
                };
            }
            return column;
        });

    const toggleItem = (id: string) => {
        setChanged(true);
        setColumns(toggleColumn(id, checkedField));
    };

    return (
        <div className="k-column-list-wrapper menu-content">
            <form className="k-lock-menu" onSubmit={(e) => onSubmit(e, columns)} onReset={onReset}>
                <div className="k-column-list">
                    {columns.map((column, idx) => {
                        if (checkedField === SettingsMenuTypes.lock && column.hideColumnLock) {
                            return null;
                        }

                        return (
                            <div key={column.label} className="k-column-list-item">
                                <input
                                    id={`column-visibility-${checkedField}-${idx}`}
                                    className="k-checkbox lock-checkbox"
                                    type="checkbox"
                                    onClick={() => {
                                        toggleItem(column.id);
                                    }}
                                    readOnly
                                    disabled={isShowMenu ? column.show && oneVisibleColumn : !column.show}
                                    checked={column[checkedField] || false}
                                />
                                <label
                                    htmlFor={`column-visibility-${checkedField}-${idx}`}
                                    className="k-checkbox-label"
                                >
                                    {column?.groupTitle ? column.groupTitle : column.label || column.title}
                                </label>
                            </div>
                        );
                    })}
                </div>
                <div className="menu-content__btn-group">
                    {/* eslint-disable-next-line react/button-has-type */}
                    <button type="reset" className="menu-content__btn">
                        {t('reset')}
                    </button>
                    <button type="submit" disabled={!changed} className="menu-content__btn">
                        {t('save')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MenuContent;
